/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Abilities from 'model/abilities/Abilities';
import MonsterOpen5e from 'model/formats/MonsterOpen5e';
import ApiService from 'services/ApiService';
import Race from 'model/character/Race';
import Class from 'model/character/Class';
import Background from 'model/character/Background';
import Spell from 'model/stats/Spell';
import SpellService from 'services/SpellService';
import BackgroundService from 'services/BackgroundService';
import ClassService from 'services/ClassService';
import RaceService from 'services/RaceService';
import FeatService from 'services/FeatService';
import CreatureOpen5e from 'model/formats/CreatureOpen5e';
import Speed from 'model/stats/Speed';
import SensesObject from 'model/stats/SensesObject';
import Open5e from 'model/formats/Open5e';

class CachedElement<T> {
  nextUrl: string = '';
  totalCount: number | null = null;
  progress = 'Loading...';
  data: T[] = [];
  constructor(url: string) {
    this.nextUrl = url;
  }

  clear() {
    this.nextUrl = '';
    this.totalCount = 0;
    this.progress = 'Loading...';
    this.data.length = 0;
    }
}

class DataModel {

  private subscribers: (() => void)[] = [];

  documents: Map<string, { name: string; licenses: string[]; }> = new Map();
  magicItems:   CachedElement<Ability>        = new CachedElement<Ability>('');
  monsters:     CachedElement<MonsterOpen5e>  = new CachedElement<MonsterOpen5e>('');
  races:        CachedElement<Race>           = new CachedElement<Race>('');
  classes:      CachedElement<Class>          = new CachedElement<Class>('');
  backgrounds:  CachedElement<Background>     = new CachedElement<Background>('');
  feats:        CachedElement<Ability>        = new CachedElement<Ability>('');
  creatures:    CachedElement<CreatureOpen5e> = new CachedElement<CreatureOpen5e>('');
  spells:       CachedElement<Spell>          = new CachedElement<Spell>('');
  spellsBySpellLevelByClass: Map<string, Map<number, Spell[]>> = new Map();

  subscribe(callback: () => void): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  notify() {
    this.subscribers.forEach((callback) => callback());
  }

  public fetchDocuments = async (cacheOnly: boolean) => {
    try {
      if (this.documents.size == 0) {
        const docs = await ApiService.fetchDocuments(cacheOnly);
        if (docs) {
          this.documents = docs;
        }
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  public clear = () => {
    this.documents.clear();
    this.magicItems.clear();
    this.monsters.clear();
    this.races.clear();
    this.classes.clear();
    this.backgrounds.clear();
    this.feats.clear();
    this.creatures.clear();
    this.spells.clear();
    this.spellsBySpellLevelByClass.clear();
    this.notify(); // Notify subscribers of changes
  }
  
  public fetchMagicItems = async (cacheOnly: boolean) => {
    await this.fetchDocuments(cacheOnly);
    await this.fetch((callback) => ApiService.getMagicItems(cacheOnly, callback),
                     this.magicItems, 'Magic Item', (data: Ability[], done: boolean) => { Abilities.setMagicItems(data, this.documents); });
  };
  public fetchSpells = async (cacheOnly: boolean) => {
    await this.fetchDocuments(cacheOnly);
    await this.fetch((callback) => ApiService.getAllSpells(cacheOnly, callback),
                     this.spells, 'Spell', (data: Spell[], done: boolean) => {
      this.spellsBySpellLevelByClass = SpellService.parseSpells(data);
      this.notify(); // Notify subscribers of changes
    });
  };
  private fetchMonsters   = async (cacheOnly: boolean) => {await this.fetch((callback) => ApiService.getAllMonsters(         cacheOnly, callback), this.monsters,    'Monster');};
  private fetchCreatures  = async (cacheOnly: boolean) => {await this.fetch((callback) => ApiService.getAllCreatures(        cacheOnly, callback), this.creatures,   'Creature');};
  private fetchRaces      = async (cacheOnly: boolean) => {await this.fetch((callback) => RaceService.fetchRaces(            cacheOnly, callback), this.races,       'Race');};
  private fetchClass      = async (cacheOnly: boolean) => {await this.fetch((callback) => ClassService.fetchClasses(         cacheOnly, callback), this.classes,     'Class');};
  private fetchBackground = async (cacheOnly: boolean) => {await this.fetch((callback) => BackgroundService.fetchBackgrounds(cacheOnly, callback), this.backgrounds, 'Background');};
  private fetchFeats      = async (cacheOnly: boolean) => {await this.fetch((callback) => FeatService.fetchFeats(            cacheOnly, callback), this.feats,       'Feat');};

  private async fetch<T>(accessorFunction: (callback: (data: T[], totalCount: number) => void) => Promise<T[]>,
                         obj: CachedElement<T>,
                         name: string,
                         onUpdate?: (data: T[], done: boolean) => void
                        ): Promise<void> {
    try {
      // Call the accessor function with a callback to update `obj`
      const results = await accessorFunction((data: T[], totalCount: number) => {
        obj.totalCount = totalCount;
        obj.progress = `Loaded ${data.length} of ${totalCount} ${name}s...`;
        obj.data = data;
        if (onUpdate) {
          onUpdate(data, false);
        }
        this.notify(); // Notify subscribers of changes
      });

      if (onUpdate) {
        onUpdate(results, true);
      }

      this.notify(); // Notify subscribers of changes

      // Final update after fetch is complete
      obj.progress = '';
      obj.data = results;
    } catch (error) {
      console.error(`Failed to fetch ${name}s`, error);
    }
  }

  private compareMonsterSources(): void {
    const SKIP = new Set(['slug', 'document__slug', 'document__title', 'document__license_url', 'document__url', 'useMm2024', 'img_main', 'page_no', 'group']);

    const isEmpty = (val: unknown): boolean => {
      if (val === null || val === undefined || val === '' || val === 'FALSE') return true;
      if (Array.isArray(val)) return val.length === 0;
      if (val instanceof Map) return val.size === 0;
      return false;
    };

    const logDiffs = (monsterName: string, path: string, v1Mon: unknown, v2Creat: unknown, set: {v1MonsterSource: string, v2CreatureSource: string}): void => {
      if (v1Mon === v2Creat) return;
      if (isEmpty(v1Mon) && isEmpty(v2Creat)) return;
      if (typeof v1Mon === 'string' && typeof v2Creat === 'string' && v1Mon.toLowerCase() ===v2Creat.toLowerCase()) return;

      if (Array.isArray(v1Mon) || Array.isArray(v2Creat)) {
        const arrV1Mon = Array.isArray(v1Mon) ? v1Mon : [];
        const arrV2Creat = Array.isArray(v2Creat) ? v2Creat : [];
        if ((arrV1Mon.length > 0 && typeof arrV1Mon[0] !== 'string') || (arrV2Creat.length > 0 && typeof arrV2Creat[0] !== 'string')) {
          for (let i = 0; i < Math.max(arrV1Mon.length, arrV2Creat.length); i++) {
            logDiffs(monsterName, `${path}[${i}]`, arrV1Mon[i], arrV2Creat[i], set);
          }
          return;
        }
        const toKey = (item: unknown) => JSON.stringify(item);
        const setV1Mon = new Set(arrV1Mon.map(toKey));
        const setV2Creat = new Set(arrV2Creat.map(toKey));
        const onlyV1Mon = new Set();
        const onlyV2Creat = new Set();
        for (const key of setV1Mon) {
          if (!setV2Creat.has(key)) {
            onlyV1Mon.add(key);
          }
        }
        for (const key of setV2Creat) {
          if (!setV1Mon.has(key)) {
            onlyV2Creat.add(key);
          }
        }
        if (onlyV1Mon.size > 0 || onlyV2Creat.size > 0) {
            console.log(`compareMonsterSources: \t${monsterName}\t${path}\t` +
                        `${set.v1MonsterSource} (v1 monster):\t${[...onlyV1Mon].join(", ")}\t` +
                        `${set.v2CreatureSource} (v2 creature):\t${[...onlyV2Creat].join(", ")}`);
        }
        return;
      }

      if (v1Mon instanceof Map || v2Creat instanceof Map) {
        const mapA = v1Mon instanceof Map ? v1Mon : new Map();
        const mapB = v2Creat instanceof Map ? v2Creat : new Map();
        for (const k of [...new Set([...mapA.keys(), ...mapB.keys()])].sort()) {
          logDiffs(monsterName, `${path}.${k}`, mapA.get(k), mapB.get(k), set);
        }
        return;
      }

      if (typeof v1Mon === 'object' && v1Mon !== null && typeof v2Creat === 'object' && v2Creat !== null) {
        const keys = new Set([...Object.keys(v1Mon as object), ...Object.keys(v2Creat as object)]);
        for (const k of [...keys].sort()) {
          logDiffs(monsterName, `${path}.${k}`, (v1Mon as Record<string, unknown>)[k], (v2Creat as Record<string, unknown>)[k], set);
        }
        return;
      }

      console.log(`compareMonsterSources: \t${monsterName}\t${path}\t` +
                  `${set.v1MonsterSource} (v1 monster):\t${JSON.stringify(v1Mon ?? '')}\t` +
                  `${set.v2CreatureSource} (v2 creature):\t${JSON.stringify(v2Creat ?? '')}`);
    };

    const normalizeText = (text: string): string => text?.replace(/[’`]/g, '\'')
                                                         .replace(/–/g, '-')
                                                         .toLowerCase() || '';

    const stripAbilities = (abilities: Ability[] | null): Ability[] =>
      (abilities ?? []).map(({ name, desc }) => ({ name: normalizeText(name), desc: normalizeText(desc) }))
                       .sort((a, b) => a.name.localeCompare(b.name));

    const normalize = (monster: MonsterOpen5e) => {
      const m = { ...monster};
      m.name = normalizeText(m.name);
      m.armor_desc = normalizeText(m.armor_desc);
      m.languages = normalizeText(m.languages);

      const monName = monster.name.endsWith(' Dragon') ? 'dragon' : monster.name.endsWith('sphinx') ? 'sphinx' : monster.name.toLowerCase();
      m.legendary_desc    = m.legendary_desc?.replace(/\[mon\]/gi, monName) || '';
      m.actions           = stripAbilities(m.actions);
      m.bonus_actions     = stripAbilities(m.bonus_actions);
      m.reactions         = stripAbilities(m.reactions);
      m.legendary_actions = stripAbilities(m.legendary_actions);
      m.special_abilities = stripAbilities(m.special_abilities);
      m.environments      = m.environments.map(m => m.replace('Ocean', 'Water')
                                                     .replace('Underwater', 'Water')
                                                     .toLowerCase()
                                                    ).sort();
      m.environments = [...new Set(m.environments)].sort(); // make unique
      //m.environments = []; // ignore environments (for now?)
      delete (m as any).v2_converted_path;

      // Speed: drop properties that are 0, null, or false, and sort the values:
      const speedObj: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(m.speed).sort()) {
        if (v !== 0 && v !== null && v !== false) {
          speedObj[k] = v;
        }
      }
      m.speed = speedObj as unknown as Speed;

      // Skills: drop zero entries (v1 API delivers a plain object, v2 delivers a Map), then normalize casing
      const skillEntries = m.skills instanceof Map
        ? [...m.skills.entries()]
        : Object.entries(m.skills as unknown as Record<string, number>);
      const titleCase = (key: string) => key.split(' ').map(w => w.length > 2 ? w.charAt(0).toUpperCase() + w.substring(1) : w).join(' ');
      m.skills = new Map(skillEntries.filter(([, v]) => v !== 0 && v !== null).map(([k, v]) => [titleCase(k), v]));
      m.size = titleCase(m.size);
      // Saving throws: coerce 0 → null so both sides compare as absent
      m.strength_save     = m.strength_save     || null;
      m.dexterity_save    = m.dexterity_save    || null;
      m.constitution_save = m.constitution_save || null;
      m.intelligence_save = m.intelligence_save || null;
      m.wisdom_save       = m.wisdom_save       || null;
      m.charisma_save     = m.charisma_save     || null;

      m.senses = m.senses.split(", ").sort().join(", ").replace(/'/g, ' ft.');
      const mon = Open5e.translateMonster(m);
      m.senses = mon.sensesObject.customString;
      const senseObj = SensesObject.parseFromString(m.senses);
      m.senses = senseObj.customString;

      return m;
    }

    const sets: {v1MonsterSource: string, v2CreatureSource: string}[] = []
    sets.push({v1MonsterSource: 'wotc-srd',  v2CreatureSource: 'srd-2014'}); //Wizards of the Coast
    //sets.push({v1MonsterSource: 'o5e',       v2CreatureSource: 'open5e'});   //Open5e Original Content
    sets.push({v1MonsterSource: 'tob',       v2CreatureSource: 'tob'});      // Tome of Beasts
    sets.push({v1MonsterSource: 'tob2',      v2CreatureSource: 'tob2'});     // Tome of Beasts 2
    sets.push({v1MonsterSource: 'tob3',      v2CreatureSource: 'tob3'});     // Tome of Beasts 3
    sets.push({v1MonsterSource: 'tob-2023',  v2CreatureSource: 'tob-2023'}); // Tome of Beasts 2023
    sets.push({v1MonsterSource: 'cc',        v2CreatureSource: 'ccdx'});     // Creature Codex
    //sets.push({v1MonsterSource: 'dmag',      v2CreatureSource: 'deepm'});    // Deep Magic for 5th Edition
    sets.push({v1MonsterSource: 'menagerie', v2CreatureSource: 'a5e-mm'});   // Monstrous Menagerie
    //sets.push({v1MonsterSource: 'a5e',       v2CreatureSource: 'a5e-ag'});   // Adventurer's Guide
    //sets.push({v1MonsterSource: 'kp',        v2CreatureSource: 'kp'});       // Kobold Press
    //sets.push({v1MonsterSource: 'dmag-e',    v2CreatureSource: 'deepmx'});   // Deep Magic Extended
    //sets.push({v1MonsterSource: 'warlock', v2CreatureSource: ''});
    //sets.push({v1MonsterSource: 'vom',       v2CreatureSource: 'vom'});      // Vault of Magic
    //sets.push({v1MonsterSource: 'toh',       v2CreatureSource: 'toh'});      // Tome of Heroes
    sets.push({v1MonsterSource: 'taldorei',  v2CreatureSource: 'tdcs'});     // Tal'dorei Campaign Setting
    sets.push({v1MonsterSource: 'blackflag', v2CreatureSource: 'bfrd'});     // Black Flag SRD
    for (const set of sets) {
      const v1MonsterMap = new Map<string, MonsterOpen5e>();
      const v2CreatureMap = new Map<string, MonsterOpen5e>();
      for (const creature of this.creatures.data) {
        if (creature.document.key === set.v2CreatureSource) {
          const m = CreatureOpen5e.convertToMonsterOpen5e(creature);
          const name = normalizeText(creature.name);
          v2CreatureMap.set(name, normalize(m));
        }
      }

      for (const monster of this.monsters.data) {
        if (monster.document__slug === set.v1MonsterSource) {
          const name = normalizeText(monster.name);
          v1MonsterMap.set(name, normalize(monster));
        }
      }

      if (v1MonsterMap.size != v2CreatureMap.size) {
        console.log(`compareMonsterSources: v1 ${set.v1MonsterSource} count=${v1MonsterMap.size}, v2 ${set.v2CreatureSource} count=${v2CreatureMap.size}`);
      }

      for (const [name, v1Mon] of v1MonsterMap) {
        const v2Creat = v2CreatureMap.get(name);
        if (!v2Creat) {
          console.log(`compareMonsterSources: '${name}' present in v1 ${set.v1MonsterSource} but not in v2 ${set.v2CreatureSource}`);
          continue;
        }
        for (const key of Object.keys(v1Mon) as (keyof MonsterOpen5e)[]) {
          if (SKIP.has(key)) continue;
          logDiffs(name, key, v1Mon[key], v2Creat[key], set);
        }
      }

      for (const name of v2CreatureMap.keys()) {
        if (!v1MonsterMap.has(name)) {
          console.log(`compareMonsterSources: '${name}' present in v2 ${set.v2CreatureSource} but not in v1 ${set.v1MonsterSource}`);
        }
      }
    }
  }

  public fetchAll = async (cacheOnly: boolean, onMonstersLoaded?: () => void) => {
    // First, load the mater list of documents:
    await this.fetchDocuments(cacheOnly);
    // Next, load the monsters, as it drives the monster selector and grid search
    await this.fetchMonsters(cacheOnly);
    await this.fetchCreatures(cacheOnly);
    // enable this code to compare v1/monsters with the v2/creatures:
    // if (!cacheOnly) {
    //   this.compareMonsterSources();
    // }
    
    // notify any watchers:
    onMonstersLoaded?.();
   
    // Then load the elements needed for the NPC modal (and also the spellbook task of the guided tour):
    // Load the quickest things to load first:
    await this.fetchRaces(cacheOnly);
    await this.fetchClass(cacheOnly);
    await this.fetchBackground(cacheOnly);
    await this.fetchFeats(cacheOnly);
    // Next, load the spells:
    await this.fetchSpells(cacheOnly);

    // Finally, load the magic items used by the 'Inventory' block:
    await this.fetchMagicItems(cacheOnly);
  }
}
export default DataModel;